import { inferAdditionalFields } from 'better-auth/client/plugins';
import { createAuthClient } from 'better-auth/react';

/**
 * Better-Auth browser client.
 *
 * `baseURL` points at the dev-proxy path `/api/auth` (vite forwards it to the
 * backend WITHOUT stripping the prefix — see vite.config.ts; Better-Auth is
 * mounted at `/api/auth/*` server-side). The session cookie is httpOnly and
 * carried automatically by the browser; the typed API client
 * (`createApiClient`) also sends it via `credentials: 'include'`.
 *
 * `inferAdditionalFields` mirrors the extra `user` fields declared on the
 * server (apps/api/src/auth.ts) so `signUp.email` accepts `attestedOver18`.
 */
const authClient = createAuthClient({
  baseURL: `${window.location.origin}/api/auth`,
  plugins: [
    inferAdditionalFields({
      user: {
        attestedOver18: { type: 'boolean', required: true },
        attestedOver18At: { type: 'string', required: false },
        locale: { type: 'string', required: false },
        orgId: { type: 'string', required: false },
      },
    }),
  ],
});

/**
 * Local surface type for the bits of the client the app uses. Declaring it
 * explicitly avoids TS2883 ("inferred type cannot be named") under this
 * package's `composite: true` build, which would otherwise try to emit
 * declarations referencing better-auth's deep internal types.
 */
interface SessionUser {
  id: string;
  email: string;
  name: string;
  emailVerified: boolean;
}
interface SessionData {
  user: SessionUser;
}
interface AuthResult {
  error: { message?: string } | null;
}

/** Fail closed if the API cannot confirm that account creation is open. */
export async function getAccountCreationStatus(): Promise<boolean> {
  try {
    const res = await fetch('/api/auth/account-creation-status', {
      credentials: 'include',
    });
    if (!res.ok) return false;
    const body = (await res.json()) as { enabled?: unknown };
    return body.enabled === true;
  } catch {
    return false;
  }
}

export const signIn = authClient.signIn as {
  email(input: { email: string; password: string }): Promise<AuthResult>;
};

// ── Phone OTP (ADR-0018 §1 / ISSUE-062) ─────────────────────────────────────

/**
 * Send a 6-digit OTP to `phoneNumber` (E.164).
 * `turnstileToken` must be obtained from the Cloudflare Turnstile widget
 * before calling this; the server reads it from `X-Turnstile-Token`.
 */
export async function sendPhoneOtp(
  phoneNumber: string,
  turnstileToken: string,
): Promise<AuthResult> {
  const res = await fetch('/api/auth/phone-number/send-otp', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Turnstile-Token': turnstileToken,
    },
    body: JSON.stringify({ phoneNumber, turnstileToken }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string };
    return { error: { message: body.message ?? `HTTP ${res.status}` } };
  }
  return { error: null };
}

/**
 * Verify the OTP sent to `phoneNumber`.
 * Pass `updatePhoneNumber: true` when adding/changing phone on an existing
 * email/Google account.
 */
export async function verifyPhoneOtp(
  phoneNumber: string,
  code: string,
  options: { updatePhoneNumber?: boolean } = {},
): Promise<AuthResult> {
  const res = await fetch('/api/auth/phone-number/verify', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phoneNumber, code, ...options }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string };
    return { error: { message: body.message ?? `HTTP ${res.status}` } };
  }
  return { error: null };
}
// ── Password management (T22.1) ──────────────────────────────────────────────

/**
 * Returns true if the current session has a credential account (email+password).
 * Phone-only users have no credential account → should use setPassword instead.
 */
export async function hasPassword(): Promise<boolean> {
  const res = await fetch('/api/auth/list-accounts', { credentials: 'include' });
  if (!res.ok) return false;
  const accounts = (await res.json()) as Array<{ providerId: string }>;
  return accounts.some((a) => a.providerId === 'credential');
}

/** Change password for an account that already has one. */
export async function changePassword(input: {
  currentPassword: string;
  newPassword: string;
  revokeOtherSessions: boolean;
}): Promise<AuthResult> {
  const res = await fetch('/api/auth/change-password', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string; code?: string };
    return { error: { message: body.message ?? `HTTP ${res.status}` } };
  }
  return { error: null };
}

/** Set a password for an account that has none yet (e.g. phone-only). */
export async function setPassword(input: { newPassword: string }): Promise<AuthResult> {
  const res = await fetch('/api/auth/set-password', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string; code?: string };
    return { error: { message: body.message ?? `HTTP ${res.status}` } };
  }
  return { error: null };
}

export const signUp = authClient.signUp as {
  email(input: {
    name: string;
    email: string;
    password: string;
    attestedOver18: boolean;
    attestedOver18At: string;
    locale?: string;
  }): Promise<AuthResult>;
};
export const signOut = authClient.signOut as () => Promise<unknown>;
export const useSession = authClient.useSession as () => {
  data: SessionData | null;
  isPending: boolean;
};
